import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface Component {
  id: string;
  name: string;
  description: string;
  type: 'Mobile App' | 'Web Application' | 'API Server';
  usage: ('Public' | 'Internal')[];
  platforms?: string[];
  technologies?: string[];
  endpoints?: string[];
}

interface Project {
  prjct_id: string;
  hndlr_tx: string;
  name: string;
  description: string;
  status: 'active' | 'completed' | 'on-hold' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  projectType: 'New Project' | 'New App Added to Existing Project' | 'Migration from Existing Application';
  startDate: string;
  estimatedCompleteDate: string;
  completeDate?: string;
  requirementsDocuments: File[];
  presentations: File[];
  components: Component[];
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  tags: string[];
}

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    status: string;
    category: string;
    priority: string;
    searchQuery: string;
  };
  sortBy: {
    field: keyof Project;
    direction: 'asc' | 'desc';
  };
}

interface ProjectActions {
  // CRUD operations
  createProject: (project: Omit<Project, 'prjct_id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  getProject: (projectId: string) => Project | undefined;
  
  // State management
  setCurrentProject: (project: Project | null) => void;
  setProjects: (projects: Project[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Filtering and sorting
  setFilter: (key: keyof ProjectState['filters'], value: string) => void;
  setSortBy: (field: keyof Project, direction: 'asc' | 'desc') => void;
  clearFilters: () => void;
  
  // Component management
  addComponent: (projectId: string, component: Omit<Component, 'id'>) => void;
  updateComponent: (projectId: string, componentId: string, updates: Partial<Component>) => void;
  removeComponent: (projectId: string, componentId: string) => void;
  
  // Search and filtering
  getFilteredProjects: () => Project[];
  getProjectById: (projectId: string) => Project | undefined;
  getProjectsByCategory: (category: string) => Project[];
  getProjectsByStatus: (status: Project['status']) => Project[];
}

export const useProjectStore = create<ProjectState & ProjectActions>()(
  devtools(
    (set, get) => ({
      // Initial state
      projects: [],
      currentProject: null,
      isLoading: false,
      error: null,
      filters: {
        status: 'all',
        category: 'all',
        priority: 'all',
        searchQuery: ''
      },
      sortBy: {
        field: 'createdAt',
        direction: 'desc'
      },

      // CRUD operations
      createProject: async (projectData) => {
        set({ isLoading: true, error: null });
        try {
          const newProject: Project = {
            ...projectData,
            prjct_id: Date.now().toString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          set((state) => ({
            projects: [...state.projects, newProject],
            isLoading: false
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create project',
            isLoading: false
          });
        }
      },

      updateProject: async (projectId, updates) => {
        set({ isLoading: true, error: null });
        try {
          set((state) => ({
            projects: state.projects.map(project =>
              project.prjct_id === projectId
                ? { ...project, ...updates, updatedAt: new Date().toISOString() }
                : project
            ),
            currentProject: state.currentProject?.prjct_id === projectId
              ? { ...state.currentProject, ...updates, updatedAt: new Date().toISOString() }
              : state.currentProject,
            isLoading: false
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update project',
            isLoading: false
          });
        }
      },

      deleteProject: async (projectId) => {
        set({ isLoading: true, error: null });
        try {
          set((state) => ({
            projects: state.projects.filter(project => project.prjct_id !== projectId),
            currentProject: state.currentProject?.prjct_id === projectId ? null : state.currentProject,
            isLoading: false
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete project',
            isLoading: false
          });
        }
      },

      getProject: (projectId) => {
        return get().projects.find(project => project.prjct_id === projectId);
      },

      // State management
      setCurrentProject: (project) => set({ currentProject: project }),
      setProjects: (projects) => set({ projects }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      // Filtering and sorting
      setFilter: (key, value) => {
        set((state) => ({
          filters: { ...state.filters, [key]: value }
        }));
      },

      setSortBy: (field, direction) => {
        set({ sortBy: { field, direction } });
      },

      clearFilters: () => {
        set({
          filters: {
            status: 'all',
            category: 'all',
            priority: 'all',
            searchQuery: ''
          }
        });
      },

      // Component management
      addComponent: (projectId, componentData) => {
        const newComponent: Component = {
          ...componentData,
          id: Date.now().toString()
        };

        set((state) => ({
          projects: state.projects.map(project =>
            project.prjct_id === projectId
              ? { ...project, components: [...project.components, newComponent] }
              : project
          )
        }));
      },

      updateComponent: (projectId, componentId, updates) => {
        set((state) => ({
          projects: state.projects.map(project =>
            project.prjct_id === projectId
              ? {
                  ...project,
                  components: project.components.map(component =>
                    component.id === componentId
                      ? { ...component, ...updates }
                      : component
                  )
                }
              : project
          )
        }));
      },

      removeComponent: (projectId, componentId) => {
        set((state) => ({
          projects: state.projects.map(project =>
            project.prjct_id === projectId
              ? {
                  ...project,
                  components: project.components.filter(component => component.id !== componentId)
                }
              : project
          )
        }));
      },

      // Search and filtering
      getFilteredProjects: () => {
        const { projects, filters, sortBy } = get();
        
        let filtered = projects.filter(project => {
          const matchesStatus = filters.status === 'all' || project.status === filters.status;
          const matchesCategory = filters.category === 'all' || project.category === filters.category;
          const matchesPriority = filters.priority === 'all' || project.priority === filters.priority;
          const matchesSearch = filters.searchQuery === '' || 
            project.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
            project.description.toLowerCase().includes(filters.searchQuery.toLowerCase());

          return matchesStatus && matchesCategory && matchesPriority && matchesSearch;
        });

        // Sort projects
        filtered.sort((a, b) => {
          const aValue = a[sortBy.field];
          const bValue = b[sortBy.field];
          
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortBy.direction === 'asc' 
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          
          if (aValue < bValue) return sortBy.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortBy.direction === 'asc' ? 1 : -1;
          return 0;
        });

        return filtered;
      },

      getProjectById: (projectId) => {
        return get().projects.find(project => project.prjct_id === projectId);
      },

      getProjectsByCategory: (category) => {
        return get().projects.filter(project => project.category === category);
      },

      getProjectsByStatus: (status) => {
        return get().projects.filter(project => project.status === status);
      }
    }),
    {
      name: 'project-store'
    }
  )
);

